if (!customElements.get('art-frame-picker')) {
  customElements.define('art-frame-picker', class extends HTMLElement {
    connectedCallback() {
      this.sources = [...this.querySelectorAll('[data-source]')];
      this.choices = this.querySelector('[data-choices]');
      this.value = this.querySelector('[data-selected-value]');
      this.sizeWrap = this.querySelector('[data-size-wrap]');
      this.sizeSelect = this.querySelector('[data-size-select]');
      this.frameWrap = this.querySelector('[data-frame-wrap]');
      this.details = this.closest('.product-details') || this.parentElement;
      this.render = this.render.bind(this);
      this.observer = new MutationObserver(() => queueMicrotask(this.render));
      this.observer.observe(this.details, { childList: true, subtree: true });
      this.setupSizeGuide();
      this.render();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    getFieldset(optionName) {
      const wanted = optionName.trim().toLowerCase();
      return [...this.details.querySelectorAll('variant-picker fieldset')].find((fieldset) => {
        const optionControl = fieldset.querySelector('[data-option-name]');
        const originalName = optionControl?.dataset.optionName?.trim().toLowerCase();
        if (originalName) return originalName === wanted;

        // Fallback for older picker markup. Visible legend text may be translated
        // by the browser, so it must never take precedence over Shopify's data.
        const legend = fieldset.querySelector('legend');
        if (!legend) return false;
        const label = [...legend.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('').trim().toLowerCase();
        return label === wanted;
      });
    }
    renderSize() {
      const sizeName = this.dataset.sizeOptionName || 'Size';
      const fieldset = this.getFieldset(sizeName);
      if (!fieldset) { this.sizeWrap.hidden = true; return; }
      this.sizeWrap.hidden = false;
      fieldset.hidden = true;
      const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
      const signature = inputs.map((input) => `${input.value}:${input.checked}:${input.dataset.optionAvailable}`).join('|');
      if (signature === this.sizeSignature) return;
      this.sizeSignature = signature;
      this.sizeSelect.replaceChildren();
      inputs.forEach((input) => {
        const option = document.createElement('option');
        option.value = input.value;
        option.textContent = input.value + (input.dataset.optionAvailable === 'false' ? ' — Sold out' : '');
        option.disabled = input.dataset.optionAvailable === 'false';
        option.selected = input.checked;
        this.sizeSelect.append(option);
      });
      this.sizeSelect.onchange = () => {
        const input = inputs.find((candidate) => candidate.value === this.sizeSelect.value);
        if (input && !input.checked) input.click();
        this.updateSizeGuide();
      };
      this.syncGuideSizes();
    }
    render() {
      this.renderSize();
      const fieldset = this.getFieldset(this.dataset.optionName);
      if (!fieldset) {
        if (this.frameWrap) this.frameWrap.hidden = true;
        this.hidden = Boolean(this.sizeWrap?.hidden);
        return;
      }
      this.hidden = false;
      if (this.frameWrap) this.frameWrap.hidden = false;
      fieldset.hidden = true;
      const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
      const signature = inputs.map((input) => `${input.value}:${input.checked}:${input.dataset.optionAvailable}`).join('|');
      if (signature === this.signature) return;
      this.signature = signature;
      this.choices.replaceChildren();
      inputs.forEach((input, index) => {
        const configured = this.sources.find((source) => source.dataset.label.trim().toLowerCase() === input.value.trim().toLowerCase()) || this.sources[index];
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'art-frame-picker__choice';
        button.title = input.value;
        button.setAttribute('aria-label', input.value);
        button.setAttribute('aria-pressed', String(input.checked));
        button.disabled = input.dataset.optionAvailable === 'false';
        const image = configured?.querySelector('img');
        if (image) button.append(image.cloneNode(true));
        else {
          const fallback = document.createElement('span');
          fallback.className = 'art-frame-picker__fallback';
          fallback.textContent = input.value.slice(0, 2).toUpperCase();
          button.append(fallback);
        }
        button.addEventListener('click', () => { if (!input.checked) input.click(); });
        this.choices.append(button);
        if (input.checked) this.value.textContent = input.value;
      });
    }
    setupSizeGuide() {
      this.guide = this.querySelector('[data-size-guide]');
      if (!this.guide) return;
      this.guideSize = this.querySelector('[data-guide-size]');
      this.furnitureWidth = 84;
      this.furnitureType = 'Sofa';
      this.guideShape = 'horizontal';
      this.guidePieces = 1;
      this.guideFramed = false;
      this.querySelector('[data-size-guide-open]')?.addEventListener('click', () => {
        this.syncGuideSizes();
        this.updateSizeGuide();
        this.guide.showModal();
        document.documentElement.classList.add('art-size-guide-open');
      });
      this.querySelector('[data-size-guide-close]')?.addEventListener('click', () => this.closeSizeGuide());
      this.guide.addEventListener('close', () => document.documentElement.classList.remove('art-size-guide-open'));
      this.guide.addEventListener('click', (event) => { if (event.target === this.guide) this.closeSizeGuide(); });
      this.guideSize?.addEventListener('change', () => {
        this.sizeSelect.value = this.guideSize.value;
        this.sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateSizeGuide();
      });
      this.querySelector('[data-guide-recommendation]')?.addEventListener('click', () => {
        if (!this.recommendedValue) return;
        this.guideSize.value = this.recommendedValue;
        this.guideSize.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.querySelector('[data-width-minus]')?.addEventListener('click', () => { this.furnitureWidth = Math.max(24, this.furnitureWidth - 6); this.updateSizeGuide(); });
      this.querySelector('[data-width-plus]')?.addEventListener('click', () => { this.furnitureWidth = Math.min(240, this.furnitureWidth + 6); this.updateSizeGuide(); });
      this.querySelectorAll('[data-furniture]').forEach((button) => button.addEventListener('click', () => {
        this.furnitureType = button.dataset.furniture;
        const defaults = { Sofa:84, Bed:76, Wall:120, Console:60, 'Dining Table':72, Fireplace:60, Desk:48, Bench:54 };
        this.furnitureWidth = defaults[this.furnitureType] || this.furnitureWidth;
        this.setPressed('[data-furniture]', button);
        this.updateSizeGuide();
      }));
      this.querySelectorAll('[data-frame]').forEach((button) => button.addEventListener('click', () => {
        this.guideFramed = button.dataset.frame === 'framed';
        this.setPressed('[data-frame]', button);
        this.updateSizeGuide();
      }));
      this.querySelectorAll('[data-shape]').forEach((button) => button.addEventListener('click', () => {
        this.guideShape = button.dataset.shape;
        this.setPressed('[data-shape]', button);
        this.updateSizeGuide();
      }));
      this.querySelectorAll('[data-pieces]').forEach((button) => button.addEventListener('click', () => {
        this.guidePieces = Number(button.dataset.pieces);
        this.setPressed('[data-pieces]', button);
        this.updateSizeGuide();
      }));
    }
    closeSizeGuide() { if (this.guide?.open) this.guide.close(); }
    setPressed(selector, activeButton) {
      this.querySelectorAll(selector).forEach((button) => button.setAttribute('aria-pressed', String(button === activeButton)));
    }
    parseSize(value) {
      const normalized = String(value || '').replace(/[”“″]/g, '"');
      const match = normalized.match(/(\d+(?:\.\d+)?)\s*(?:"|in)?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      return match ? { width:Number(match[1]), height:Number(match[2]) } : null;
    }
    formatSize(value) {
      const size = this.parseSize(value);
      return size ? `${size.width}\"W x ${size.height}\"H` : value;
    }
    syncGuideSizes() {
      if (!this.guideSize || !this.sizeSelect) return;
      const signature = [...this.sizeSelect.options].map((option) => `${option.value}:${option.disabled}`).join('|');
      if (signature !== this.guideSizeSignature) {
        this.guideSizeSignature = signature;
        this.guideSize.replaceChildren(...[...this.sizeSelect.options].map((source) => {
          const option = source.cloneNode(true);
          option.textContent = this.formatSize(source.value) + (source.disabled ? ' — SOLD OUT' : '');
          return option;
        }));
      }
      this.guideSize.value = this.sizeSelect.value;
    }
    recommendedSize() {
      const targets = { Sofa:.65, Bed:.7, Wall:.42, Console:.78, 'Dining Table':.72, Fireplace:.78, Desk:.8, Bench:.82 };
      const target = this.furnitureWidth * (targets[this.furnitureType] || .65);
      const options = [...(this.guideSize?.options || [])].filter((option) => !option.disabled).map((option) => ({ option, size:this.parseSize(option.value) })).filter((item) => item.size);
      return options.sort((a, b) => Math.abs(a.size.width - target) - Math.abs(b.size.width - target))[0] || null;
    }
    updateSizeGuide() {
      if (!this.guide) return;
      this.syncGuideSizes();
      const parsed = this.parseSize(this.guideSize?.value) || { width:54, height:36 };
      const recommended = this.recommendedSize();
      this.recommendedValue = recommended?.option.value || '';
      [...(this.guideSize?.options || [])].forEach((option) => {
        const source = [...this.sizeSelect.options].find((item) => item.value === option.value);
        option.textContent = this.formatSize(option.value) + (source?.disabled ? ' — SOLD OUT' : '') + (option.value === this.recommendedValue ? ' — RECOMMENDED' : '');
      });
      const output = this.querySelector('[data-width-output]');
      if (output) output.textContent = this.furnitureWidth;
      const artworks = this.querySelector('[data-guide-artworks]');
      const furnitureVisualWidths = { Sofa:58, Bed:68, Wall:72, Console:48, 'Dining Table':58, Fireplace:48, Desk:48, Bench:45 };
      const furnitureVisualWidth = furnitureVisualWidths[this.furnitureType] || 58;
      if (artworks) {
        const relativeWidth = Math.min(82, Math.max(14, parsed.width / this.furnitureWidth * furnitureVisualWidth));
        artworks.style.setProperty('--guide-art-width', `${relativeWidth}%`);
        artworks.style.setProperty('--guide-art-ratio', `${parsed.width} / ${parsed.height}`);
        artworks.dataset.shape = this.guideShape;
        artworks.dataset.pieces = String(this.guidePieces);
        artworks.dataset.framed = String(this.guideFramed);
      }
      const furniture = this.querySelector('[data-guide-furniture]');
      if (furniture) furniture.dataset.type = this.furnitureType.toLowerCase().replace(/\s+/g, '-');
      const artHeight = this.querySelector('[data-art-height]');
      if (artHeight) artHeight.textContent = `${parsed.height}\" H`;
      const artWidth = this.querySelector('[data-art-width]');
      if (artWidth) artWidth.textContent = `${parsed.width}\" W`;
      const furnitureMeasure = this.querySelector('[data-furniture-measure]');
      if (furnitureMeasure) {
        furnitureMeasure.textContent = `${this.furnitureWidth}\" W`;
        furnitureMeasure.style.width = `${furnitureVisualWidth}%`;
      }
      const caption = this.querySelector('[data-guide-caption]');
      if (caption) caption.textContent = `${parsed.width}\"W × ${parsed.height}\"H · ${this.guidePieces === 1 ? 'Single' : `Set of ${this.guidePieces}`} · ${this.furnitureType} ${this.furnitureWidth}\" wide`;
      const recommendation = this.querySelector('[data-guide-recommendation]');
      if (recommendation) {
        recommendation.textContent = recommended ? `${this.formatSize(recommended.option.value)} ↗` : '';
        recommendation.hidden = !recommended;
      }
    }
  });
}
